import { useEffect, useState } from "react";
import { errorMessage, request, RequestError } from "../lib/api";
import { getRole, getToken } from "../lib/auth";
import { track } from "../lib/events";
import type { PDDaySummary, PDDaysResponse } from "../types";
import { readPDDaysCache, writePDDaysCache } from "./pdDaysCache";

export interface GroupedPDDays {
  active: PDDaySummary[];
  past: PDDaySummary[];
  future: PDDaySummary[];
}

export interface PDDaysState {
  groups: GroupedPDDays | null;
  /** Flat, unsorted list — the calendar needs every session, ungrouped. */
  all: PDDaySummary[];
  /** Session id → 1-based ordinal across the facilitator's whole cohort, by start date ascending. */
  dayNumbers: Record<string, number>;
  total: number;
  loading: boolean;
  error: string;
}

const emptyState: PDDaysState = {
  groups: null,
  all: [],
  dayNumbers: {},
  total: 0,
  loading: true,
  error: "",
};

function stateForDays(days: PDDaySummary[]): PDDaysState {
  return {
    groups: groupPDDays(days),
    all: days,
    dayNumbers: numberPDDays(days),
    total: days.length,
    loading: false,
    error: "",
  };
}

export function numberPDDays(days: PDDaySummary[]): Record<string, number> {
  const ordered = [...days].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );
  const numbers: Record<string, number> = {};
  ordered.forEach((day, index) => {
    numbers[day.id] = index + 1;
  });
  return numbers;
}

// A session whose scheduled end has passed is past, even if it started
// today — the end time is checked here too, not just the server's status,
// so a list fetched mid-session still files it correctly once it ends.
// A running session is "active" wherever its start date falls — a PD day
// that overran midnight is still the one the facilitator is standing in.
// Everything else groups by the server's own past/today/upcoming verdict.
export function groupPDDays(days: PDDaySummary[], now: number = Date.now()): GroupedPDDays {
  const groups: GroupedPDDays = { active: [], past: [], future: [] };
  for (const day of days) {
    if (day.status === "ended" || new Date(day.end).getTime() <= now) groups.past.push(day);
    else if (day.status === "in_progress" || day.when === "today") groups.active.push(day);
    else if (day.when === "past") groups.past.push(day);
    else groups.future.push(day);
  }
  return groups;
}

export function usePDDays(): PDDaysState {
  const token = getToken() ?? "";
  const endpoint = getRole() === "teacher" ? "/api/teacher/pd-days" : "/api/facilitator/pd-days";
  const [state, setState] = useState<PDDaysState>(() => {
    const cached = readPDDaysCache();
    return cached?.token === token && cached.endpoint === endpoint ? cached.state : emptyState;
  });

  useEffect(() => {
    let active = true;
    track("page_opened", { payload: { page: "pd_days" } });
    request<PDDaysResponse>(endpoint)
      .then(({ pd_days: days }) => {
        if (!active || getToken() !== token) return;
        const next = stateForDays(days);
        writePDDaysCache({ token, endpoint, state: next });
        setState(next);
      })
      .catch((cause) => {
        if (!active) return;
        if (cause instanceof RequestError && cause.status === 401) writePDDaysCache(null);
        setState((current) =>
          current.groups && !(cause instanceof RequestError && cause.status === 401)
            ? current
            : { ...emptyState, loading: false, error: errorMessage(cause) }
        );
      });
    return () => {
      active = false;
    };
  }, [endpoint, token]);

  return state;
}

/** The 1-based PD Day number for one session, or undefined until the list loads. */
export function usePDDayNumber(sessionID: string): number | undefined {
  return usePDDays().dayNumbers[sessionID];
}
